import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { SearchInput } from "@/components/ui/search-input";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import { supabase } from "@/integrations/supabase/client";
import { useUserPermissions } from "@/hooks/use-user-permissions";
import { FormularioCard } from "@/components/avaliacoes/FormularioCard";
import { FormularioFormModal } from "@/components/avaliacoes/FormularioFormModal";
import type { EvaluationFormWithItems } from "@/types/evaluation-form";

export default function FormulariosAvaliacao() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedFormulario, setSelectedFormulario] = useState<EvaluationFormWithItems | null>(null);
  const { profileName } = useUserPermissions();
  const queryClient = useQueryClient();

  const isAdmin = ["admin", "membro"].includes(profileName?.toLowerCase() || "");

  const { data: formularios = [], isLoading, isError, refetch } = useQuery({
    queryKey: ["evaluation_forms"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_evaluation_forms_with_items")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Erro ao buscar formulários:", error);
        throw error;
      }

      // Parse items from JSON string if needed
      return (data || []).map((form: any) => ({
        ...form,
        items: typeof form.items === 'string' ? JSON.parse(form.items) : form.items || [],
      })) as EvaluationFormWithItems[];
    },
  });

  const filteredFormularios = formularios.filter((form) =>
    form.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["evaluation_forms"] });
    setIsCreateModalOpen(false);
    setSelectedFormulario(null);
  };

  const handleEditFormulario = (formulario: EvaluationFormWithItems) => {
    setSelectedFormulario(formulario);
    setIsCreateModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setSelectedFormulario(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Formulários de Avaliação"
        subtitle="Gerencie os formulários dinâmicos usados para avaliar freelancers"
        actions={
          isAdmin && (
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Formulário
            </Button>
          )
        }
      />

      <SearchInput
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Buscar formulários..."
      />

      {isLoading ? (
        <LoadingState variant="grid" />
      ) : isError ? (
        <ErrorState onRetry={() => refetch()} />
      ) : filteredFormularios.length === 0 ? (
        <EmptyState
          icon={FileText}
          title={
            searchTerm
              ? "Nenhum formulário encontrado"
              : "Nenhum formulário cadastrado ainda"
          }
          description={
            searchTerm
              ? "Nenhum formulário corresponde a esse termo."
              : "Crie um formulário para começar a avaliar freelancers."
          }
          action={
            isAdmin && !searchTerm ? (
              <Button
                variant="outline"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Criar primeiro formulário
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {filteredFormularios.map((formulario) => (
            <FormularioCard
              key={formulario.id}
              formulario={formulario}
              onEdit={isAdmin ? () => handleEditFormulario(formulario) : undefined}
            />
          ))}
        </div>
      )}

      <FormularioFormModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleCreateSuccess}
        formulario={selectedFormulario}
      />
    </div>
  );
}

